import React, { ReactNode, useState } from 'react';
import styled from '@emotion/styled';
import { useParams } from 'react-router-dom';
import { useEditAdminEvent } from './useEditAdminEvent';
import { DeleteButton, Form, FormGrid, OccupyFreeSpace, SaveButton, TabPanel, TabsHeader, TitleLink, Tooltip, useSaveable, useTabsState } from '../../../toolympus/components/primitives';
import { Button, Chip, IconButton, Typography } from '@mui/material';
import { CallMade, DeleteOutlined } from '@mui/icons-material';
import { useItemWithControls } from '../../../toolympus/api/useWithControls';
import { PowerEditorBase } from '../../../toolympus/components/PowerDoc';
import { LocaleSwitch } from '../../../toolympus/components/LocalizedRecords';
import { MediafileEdit } from '../../../toolympus/components/schemed/MediafileEdit';
import { MediaLibProvider } from '../../../toolympus/components/medialib';
import { DateAndTimeEdit } from '../../../toolympus/components/schemed/TimeEdit';
import { CMSEditorUnwrapped, SiteBaseUrl } from '../../CMS';
import { PowerEditorSettingsProvider } from '../../../toolympus/components/PowerDoc/PowerEditorSettingsContext';
import { EventRegistrationsList } from './EventRegistrationsList';
import { SimpleMultiAttributeEditor } from '../../../toolympus/components/primitives/SimpleMultiAttributeEditor';
import { EventDiscountCodesList } from './EventDiscountCodesList';

interface Props {
  isArchive?: boolean;
}

export const Locales: [string,ReactNode][] = [["en", "en"], ["ru", "ru"]];

export const getTranslationField = (f: string, lang: string): string => {
  if(lang !== "ru") {
    return f;
  }
  return {
    title: "title_ru",
    location: "location_ru",
    organiser_title: "organiser_title_ru",
    description: "description_ru",
    description_short: "description_short_ru",
    price: "price_ru",
    price_currency: "price_currency_ru",
    registration_external_url: "registration_external_url_ru",
  }[f] || f;
}


export const FocusControlsPanel = styled(FormGrid)`
  border-radius: 16px;
  background: #da5a3630;
  padding: 12px;
  box-shadow: ${props => props.theme.shadows[2]};
`;
FocusControlsPanel.defaultProps = { columns: "1fr", noMargin: true };


export const EventAdminPage = (props: Props) => {
  const { id } = useParams<{ id: string }>();
  const data = useEditAdminEvent(+id);

  const { controls } = useItemWithControls(data, { schema: data.schema });

  useSaveable(data);

  const [lang,setLang] = useState<string>("en");

  
  const tabs = useTabsState([
    ["attributes","Attributes"],
    ["content","Content"],
    ["registrations","Registrations"],
    ["discount_codes","Discount Codes"],
  ], "attributes", "tab");


  return (
    <MediaLibProvider apiPath="/api/media" spaceId={"riac_event_media"} urlPrefix="/media">
      <Form
        title={<TitleLink to={props.isArchive ? "/archive-events" : "/events"}>Event</TitleLink>}
        headerItems={<>
          {props.isArchive && <Chip color="warning" size="small" label="archive" />}
          <OccupyFreeSpace />
          {data.hasChanges && <SaveButton action={() => data.save()} />}

          <a href={`${SiteBaseUrl}/open?entity=event&lang=${lang}&slugOrId=${id}`} target="_blank" rel="noreferrer noopener nofollow">
            <Tooltip text_id="cms.form.open_site">
              <IconButton size="small"><CallMade /></IconButton>
            </Tooltip>
          </a>

          <Button size="small" onClick={() => data.update({ year: null })}>restore</Button>

          <LocaleSwitch availableLocales={Locales} locale={lang} setLocale={setLang} />
          <DeleteButton
            remove={() => data.remove()}
            title="Remove event?"
            button={<IconButton size="small"><DeleteOutlined /></IconButton>}
            />
        </>}
        >
        <FormGrid columns={props.isArchive ? "1fr 150px 150px" : "1fr 150px"} style={{ alignItems: "flex-end" }}>
          {controls([
            [getTranslationField("title", lang)],
            ["is_published"],
            props.isArchive ? ["year", { label: "Year" }] : null,
          ])}

          
        </FormGrid>

        <TabsHeader state={tabs} />

        <TabPanel state={tabs.forTab("attributes")}>
          <FormGrid columns="2fr 1fr" forceEvenColumns>
            <FormGrid columns="1fr" noMargin>
              <FormGrid columns="1fr 1fr 1fr 1fr" forceEvenColumns noMargin>
                <DateAndTimeEdit
                  field="start_datetime"
                  row={data.data}
                  onChange={(o,c) => data.update(c)}
                  schema={data.schema.start_datetime}
                  dateSchema={{ label: "Date"}}
                  errors={data.errors}
                  />
                <DateAndTimeEdit
                  field="end_datetime"
                  row={data.data}
                  onChange={(o,c) => data.update(c)}
                  schema={data.schema.end_datetime}
                  noDate
                  errors={data.errors}
                  />

                {controls([
                  ["slot_order"],
                ])}
              </FormGrid>

              

              <FormGrid columns="1fr 1fr" forceEvenColumns noMargin alignItems="end">
                {controls([
                  [getTranslationField("location", lang)],
                  ["location_url"],
                  ["event_format"],
                  ["languages"],
                ])}
              </FormGrid>

              <SimpleMultiAttributeEditor
                label="Organiser companies"
                value={lang === "ru" ? data.data.organiser_companies_ru : data.data.organiser_companies}
                fields={[
                  ["title", { controlProps: { autoFocus: true }}],
                  ["url"],
                ]}
                displayText={o => o.title}
                schema={{
                  title: { label: "Organiser"},
                  url: { label: "URL"},
                }}
                update={v => lang === "ru" ? data.update({ organiser_companies_ru: v }) : data.update({ organiser_companies: v })}
                />

              {controls([
                  ["organisers_ids", { autoComplete: true }],
                ])}

              

              <FormGrid columns="repeat(3,1fr)">
                {controls([
                  ["created_at", { disabled: true, utcToLocal: true }],
                  ["updated_at", { disabled: true, utcToLocal: true }],
                  ["published_at", { disabled: true, utcToLocal: true }],
                  ["created_by", { disabled: true }],
                  ["updated_by", { disabled: true }],
                  ["published_by", { disabled: true }],
                ])}
              </FormGrid>
            </FormGrid>

            <FormGrid columns="1fr" noMargin>
              <FocusControlsPanel columns="1fr" noMargin>
                {controls([
                  ["registration_status"],
                  data.data.registration_status === "external_link"
                    ? [getTranslationField("registration_external_url", lang)]
                    : null,
                  ["price_option"],
                ])}

                {data.data.price_option === "paid" && <>
                  <FormGrid columns="1fr 1fr" noMargin forceEvenColumns>
                    {controls([
                      [getTranslationField("price", lang)],
                      [getTranslationField("price_currency", lang)],
                    ])}
                  </FormGrid>

                  {controls([
                    ["allow_free_requests"],
                    ["receipt_title"],
                    ["payment_tax_option"],
                    ])}
                </>}

              </FocusControlsPanel>

              <MediafileEdit
                field="cover_image"
                row={data.data}
                onChange={(o,c) => data.update(c)}
                schema={data.schema["cover_image"]}
                />

              {controls([
                ["show_in_banners"],
                ["calendar_classes"],
                ])}
            </FormGrid>
          </FormGrid>



          
        </TabPanel>

        <TabPanel state={tabs.forTab("content")}>
          <Typography variant="caption" color="textSecondary">Description (short)</Typography>
          {lang === "ru"
            ? <PowerEditorBase
                key={`${data.data._id}_dsru_${data.loadBumper || ""}`}
                content={data.data.description_short_ru}
                update={v => data.update({ description_short_ru: v })}
                editorElementId="doc-editor"
                />
            : <PowerEditorBase
                key={`${data.data._id}_ds_${data.loadBumper || ""}`}
                content={data.data.description_short}
                update={v => data.update({ description_short: v })}
                editorElementId="doc-editor"
                />}
          

          <Typography variant="caption" color="textSecondary">Description (full)</Typography>
          <PowerEditorSettingsProvider
            EditorComponent={CMSEditorUnwrapped}>

            
            {lang === "ru"
              ? <CMSEditorUnwrapped
                  key={`${data.data._id}_dfru_${data.loadBumper || ""}`}
                  content={data.data.description_ru}
                  update={v => data.update({ description_ru: v })}
                  editorElementId="doc-editor"
                  />
              : <CMSEditorUnwrapped
                  key={`${data.data._id}_df_${data.loadBumper || ""}`}
                  content={data.data.description}
                  update={v => data.update({ description: v })}
                  editorElementId="doc-editor"
                  />}
          </PowerEditorSettingsProvider>
        </TabPanel>


        <TabPanel state={tabs.forTab("registrations")}>
          <EventRegistrationsList event={data.data} isPaid={data.data.price_option === "paid"} />
        </TabPanel>

        <TabPanel state={tabs.forTab("discount_codes")}>
          <EventDiscountCodesList eventId={data.data._id} />
        </TabPanel>
      </Form>
    </MediaLibProvider>
  );
}



export const EventArchivePage = () => {
  return (
    <EventAdminPage isArchive />
  )
}
